import React from 'react';
import ReactECharts from 'echarts-for-react';
import {
  getInternalSEColor,
  buildChartTheme,
  subtleHorizontalSplitLine,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';

const LineChartDemos = ({
  data,
  range,
  display,
  renderZeroWhenEmpty = false,
  zeroXAxis = [],
  zeroSeriesNames = ['APJ', 'EMEA', 'NA', 'LATAM'],
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();
  const theme = buildChartTheme(isLaptopCompact);

  const hasData = !!(data?.xData?.length && data?.series?.length);
  if (!hasData && !renderZeroWhenEmpty) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }

  const normalizedData = hasData
    ? data
    : {
        xData: zeroXAxis.length ? zeroXAxis : [''],
        series: zeroSeriesNames.map((name, index) => ({
          name,
          data: new Array((zeroXAxis.length ? zeroXAxis : ['']).length).fill(0),
          color: getInternalSEColor(index),
        })),
      };
  const isAllZero = normalizedData.series.every((item) =>
    (item?.data || []).every((value) => Number(value) === 0)
  );

  const options = {
    animation: false,
    grid: theme.gridStandard,
    tooltip: {
      ...theme.tooltipBase,
      trigger: 'axis',
      formatter: (params = []) => {
        const axis = params[0]?.axisValueLabel || params[0]?.axisValue || '';
        const rows = params.map((item) => (
          `${item.marker} ${item.seriesName}: ${item.value ?? 0} runs`
        ));
        return [axis, ...rows].filter(Boolean).join('<br/>');
      },
    },
    legend: {
      ...theme.legendBase,
      bottom: 0,
      left: 'center',
      icon: 'roundRect',
      itemGap: isLaptopCompact ? 12 : 14,
      formatter: (name) => truncateChartLabel(name, 20),
    },
    xAxis: {
      type: 'category',
      data: normalizedData.xData,
      boundaryGap: false,
      axisLabel: { ...theme.axisLabel, fontWeight: 500 },
    },
    yAxis: {
      type: 'value',
      name: 'Narrative Runs',
      nameTextStyle: theme.axisNameStyle,
      axisLabel: theme.axisLabel,
      min: 0,
      max: isAllZero ? 1 : undefined,
      splitLine: subtleHorizontalSplitLine,
    },
    series: normalizedData.series.map((item, index) => ({
      name: item.name,
      data: item.data,
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 3, color: item.color || getInternalSEColor(index) },
      itemStyle: { color: item.color || getInternalSEColor(index) },
    })),
    title: {
      show: range === 'Quarter' || range === 'LastQuarter',
      text: display,
      left: 'center',
      bottom: isLaptopCompact ? 32 : 36,
      textStyle: { fontSize: theme.fs(12), color: '#6B7280', fontWeight: 'normal' },
    },
  };

  return (
    <ReactECharts
      option={options}
      style={{ height: `${isLaptopCompact ? 300 : 320}px`, width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default LineChartDemos;
